import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { User } from '@/types';
import { authService } from '@/services/auth.service';
import type { ApiUser, LoginRequest } from '@/types/api';

// ============================================================================
// TYPES
// ============================================================================

interface AuthContextType {
  // Estado
  currentUser: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  
  // Roles
  isSuperAdmin: boolean;
  isCompanyAdmin: boolean;
  canManageDevices: boolean;
  
  // Acciones
  login: (credentials: LoginRequest) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

// ============================================================================
// HELPER: Convertir ApiUser a User del frontend
// ============================================================================

function apiUserToUser(apiUser: ApiUser): User {
  return {
    id: apiUser.id,
    email: apiUser.email,
    name: apiUser.name,
    role: apiUser.role,
    companyId: apiUser.companyId || undefined,
    createdAt: new Date(apiUser.createdAt),
    updatedAt: new Date(apiUser.updatedAt),
  };
}

// ============================================================================
// CONTEXT
// ============================================================================

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ============================================================================
// PROVIDER
// ============================================================================

export function AuthProvider({ children }: { children: ReactNode }) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Computed values
  const isAuthenticated = currentUser !== null;
  const isSuperAdmin = currentUser?.role === 'superadmin';
  const isCompanyAdmin = currentUser?.role === 'company_admin';
  const canManageDevices = isSuperAdmin;

  /**
   * Cargar usuario actual desde el servidor
   */
  const loadUser = useCallback(async () => {
    // Si no hay token, no hay usuario
    if (!authService.isAuthenticated()) {
      setCurrentUser(null);
      setIsLoading(false);
      return;
    }

    try {
      const response = await authService.getCurrentUser();
      
      if (response.success && response.data) {
        setCurrentUser(apiUserToUser(response.data));
      } else {
        // Token inválido o expirado
        authService.clearSession();
        setCurrentUser(null);
      }
    } catch (error) {
      console.error('Error loading user:', error);
      authService.clearSession();
      setCurrentUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Login
   */
  const login = useCallback(async (credentials: LoginRequest): Promise<{ success: boolean; error?: string }> => {
    setIsLoading(true);
    
    try {
      const response = await authService.login(credentials);
      
      if (response.success && response.data) {
        // Convertir y guardar el usuario
        setCurrentUser(apiUserToUser(response.data.user));
        return { success: true };
      } else {
        return { 
          success: false, 
          error: response.error?.message || 'Error al iniciar sesión' 
        };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        error: 'Error de conexión. Verifica tu conexión a internet.' 
      };
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Logout
   */
  const logout = useCallback(async () => {
    setIsLoading(true);
    
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setCurrentUser(null);
      setIsLoading(false);
    }
  }, []);

  /**
   * Refrescar usuario
   */
  const refreshUser = useCallback(async () => {
    await loadUser();
  }, [loadUser]);

  /**
   * Cargar usuario al montar el componente
   */
  useEffect(() => {
    loadUser();
  }, [loadUser]);

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        isLoading,
        isAuthenticated,
        isSuperAdmin,
        isCompanyAdmin,
        canManageDevices,
        login,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// ============================================================================
// HOOK
// ============================================================================

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
